'use client'

import { useState, useEffect, useContext, useRef, useCallback } from 'react'
import { CreateNewRequest, FormattedRequestObject, Message, SendNewMessage, StreamEvent, StreamEventBase } from '@/lib/types'
import { toast } from 'sonner'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { v4 as uuidv4 } from 'uuid'
import { models, ModelId } from '@/lib/models/model-list'
import { formatRequestObject } from '@/lib/request/format-request-object'
import { useUserContext } from '@/components/providers'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { ERROR_DEFINITIONS } from '@/lib/types'

// Supabase queries
import { fetchConversationMessages, bookmarkMessage } from '@/lib/supabase/queries/message-supabase-query'
import { bookmarkConversation, upsertChatTitle } from '@/lib/supabase/queries/conversation-supabase-query'
import { generate } from '@/lib/request/generate'
import { checkUserEligibility } from '@/lib/request/check-user-eligibility'
import { checkOrCreateUserIdentifiers } from '@/lib/use-manager-helper-functions/guest-user-uuid'
import { addLogToMessage } from '@/lib/use-manager-helper-functions/add-log-to-message'
import { dispatchNewEvent } from '@/lib/utils/dispatch-event'
import { useConversationCache } from './use-conversation-cache'
import { defaultUser } from '@/lib/user/default-user'
import { ToolCallStartEventSchema } from '@/lib/types/stream-events'


export function useManager() {
    const { userContextValue, setUserContextValue, isUserAuthenticated } = useUserContext()
    const pathname = usePathname()
    const { getCachedConversation, updateCachedConversation, synchronizeWithDb, removeMessageFromCache, forceSavePendingDebouncedUpdates } = useConversationCache()

    // State management for messages, bookmarks, errors and loading states
    const [conversationId, setConversationId] = useState<string>('')
    const [messages, setMessages] = useState<Message[]>([])
    const messagesRef = useRef<Message[]>([])
    const [isBookmarked, setIsBookmarked] = useState(false)
    const [managerError, setManagerError] = useState<string | null>(null)
    const [isInitializing, setIsInitializing] = useState(false) // Tracks initial data loading
    const [requestInProgress, setRequestInProgress] = useState(false)
    const [conversationTitle, setConversationTitle] = useState("New Chat")
    const [conversationSystemPrompt, setConversationSystemPrompt] = useState<string | null>(null)
    const { getLocalStorageValue, deleteLocalStorageValue, setLocalStorageValue } = useLocalStorage()
    const [keepFilesInRequest, setKeepFilesInRequest] = useState(true)
    
    // New resumable streaming state
    const [canCancelStream, setCanCancelStream] = useState(false)
    const [isStreamResumable, setIsStreamResumable] = useState(false)
    // const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null)
    const latestOnFailureRef = useRef<((error: string) => void) | null>(null)

    const searchParams = useSearchParams()

    // Remove model selection logic - this is now handled by use-prompt-form
    // const { processNextChunk, resetState, isDone } = useStreamedAudio()

    // Consume stream response - shared by both makeLLMRequest and attemptStreamResumption
    const consumeStreamResponse = useCallback(async (response: Response) => {
        if (!response.body) {
            throw new Error('Response body is null')
        }

        const reader = response.body.getReader()
        // readerRef.current = reader
        const decoder = new TextDecoder()

        const startTime = Date.now()

        try {
            while (true) {
                const { done, value } = await reader.read()
                if (done) break
                // console.log('ms since last chunk: ', Date.now() - startTime)
                // startTime = Date.now()
                // console.log('new chunk at time: ', new Date().toISOString().slice(11, 23))

                const text = decoder.decode(value)
                const lines = text.split('\n').filter(line => line.trim())

                for (const line of lines) {
                    const cleanedLine = line.startsWith('data: ') ? line.substring(6) : line

                    if (cleanedLine === '[DONE]' || cleanedLine === '' || cleanedLine.trim() === '') {
                        continue
                    }

                    try {
                        const parsedData = JSON.parse(cleanedLine)
                        const { type, data } = parsedData

                        if (type === 'data-stream' && data) {
                            handleNewStreamEvent(data)
                        }
                    } catch (parseError) {
                        console.warn('Failed to parse line:', cleanedLine, parseError)
                    }
                }
            }
        } finally {
            setRequestInProgress(false)
            setCanCancelStream(false)
            // readerRef.current = null
        }
    }, [])






    const handleNewStreamEvent = (event: StreamEvent) => {
        const messageId = event.data.messageId
        
        // console.log(event.type + ' - ', event.data)
        switch (event.type) {
            case 'content':
                setMessages(prev => prev.map(msg => {
                    if (msg.id !== messageId) return msg
                    
                    return {
                        ...msg,
                        content: event.data.content
                    }
                }))
                break
            case 'content-delta':
                setMessages(prev => prev.map(msg => {
                        if (msg.id !== messageId) return msg
                                                
                        return {
                            ...msg,
                            content: msg.content + event.data.delta
                        }
                    }))
                break
            case 'reasoning':
                setMessages(prev => prev.map(msg => {
                    if (msg.id !== messageId) return msg
                    
                    return {
                        ...msg,
                        reasoning: event.data.reasoning
                    }
                }))
                break
            case 'reasoning-delta':
                setMessages(prev => prev.map(msg => {
                    if (msg.id !== messageId) return msg

                    const current = msg.reasoning || ''
                    const incoming = event.data.delta || ''
                    if (!incoming) return msg

                    // If server accidentally sends full text as delta, append only the new tail
                    if (incoming.startsWith(current)) {
                        return {
                            ...msg,
                            reasoning: current + incoming.slice(current.length)
                        }
                    }

                    // If current already ends with incoming, ignore
                    if (current.endsWith(incoming)) {
                        return msg
                    }

                    // Compute maximal overlap between current suffix and incoming prefix
                    const maxOverlap = Math.min(current.length, incoming.length)
                    let overlap = 0
                    for (let i = maxOverlap; i > 0; i--) {
                        if (current.slice(-i) === incoming.slice(0, i)) {
                            overlap = i
                            break
                        }
                    }

                    const trimmed = incoming.slice(overlap)
                    if (!trimmed) return msg

                    return {
                        ...msg,
                        reasoning: current + trimmed
                    }
                }))
                break
            case 'status':
                setMessages(prev => prev.map(msg => {
                    if (msg.id !== messageId) return msg

                    // Preserve failed state if an error was already recorded
                    if (msg.error?.errorType || msg.info.status.status === 'failed') {
                        return {
                            ...msg,
                            info: {
                                ...msg.info,
                                status: {
                                    ...msg.info.status,
                                    isLoading: false,
                                    status: 'failed',
                                    steps: event.data.status.steps || []
                                }
                            }
                        }
                    }

                    return {
                        ...msg,
                        info: { 
                            ...msg.info, 
                            status: event.data.status
                        }
                    }
                }))

                if (event.data.status.status === 'completed' || event.data.status.status === 'failed') {
                    setRequestInProgress(false)
                    setCanCancelStream(false)
                    // Fallback: if we reached a failed status without a dedicated error event, notify listeners once
                    if (event.data.status.status === 'failed' && latestOnFailureRef.current) {
                        latestOnFailureRef.current('Request failed')
                    }
                    latestOnFailureRef.current = null
                }
                break
            case 'files':
                setMessages(prev => prev.map(msg => {
                    if (msg.id !== messageId) return msg
                    
                    return {
                        ...msg,
                        files: event.data.files
                    }
                }))
                break
            case 'temporaryFiles':
                setMessages(prev => prev.map(msg => {
                    if (msg.id !== messageId) return msg
                    
                    return {
                        ...msg,
                        temporaryFiles: event.data.temporaryFiles
                    }
                }))
                break
            case 'enhancedContent':
                setMessages(prev => prev.map(msg => {
                    if (msg.id !== messageId) return msg
                    
                    return {
                        ...msg,
                        enhancedContent: event.data.enhancedContent
                    }
                }))
                break
            case 'toolInvocation':
                setMessages(prev => prev.map(msg => {
                    if (msg.id !== messageId) return msg
                    
                    return {
                        ...msg,
                        toolInvocation: event.data.toolInvocation
                    }
                }))
                break
            case 'error':
                // {
                    const errorKey = event.data.errorType as keyof typeof ERROR_DEFINITIONS
                    const errorMessage = ERROR_DEFINITIONS[errorKey]?.message || event.data.errorType

                    // Notify prompt form so it can restore the user's draft (no thrown error; callback-based)
                    latestOnFailureRef.current?.(errorMessage)
                    latestOnFailureRef.current = null

                    setMessages(prev => prev.map(msg => {
                        if (msg.id !== messageId) return msg

                        return {
                            ...msg,
                            info: {
                                ...msg.info,
                                status: {
                                    ...msg.info.status,
                                    isLoading: false,
                                    status: 'failed',
                                    steps: []
                                }
                            },
                            error: {
                                message: errorMessage,
                                errorType: event.data.errorType
                            }
                        }
                    }))
                // }
                // Do not persist transient error-only assistant messages in cache
                if (conversationId) {
                    removeMessageFromCache(conversationId, messageId)
                }
                break
            case 'title':
                handleUpdateTitle(event.data.title, false) // don't save to db, server did it
                break
            case 'user-balance':
                setUserContextValue('balance', event.data.balance, false)
                break
            case 'checkpoint':
                setMessages(prev => prev.map(msg => {
                    
                    if (msg.id !== messageId) return msg
                    
                    return event.data.assistantMessage
                }))
                break
        }
    }



















































































































































































































































































    // Attempt stream resumption - defined early for use in useEffect
    const attemptStreamResumption = useCallback(async (conversationId: string) => {
        if (!conversationId) return false

        console.log('🔍 Attempting to resume stream for conversation:', conversationId)

        try {
            const response = await fetch(`/api/generate/${conversationId}/stream`)
            console.log('🔍 response', response)

            if (response.status === 204) {
                console.log('📝 No existing stream found')
                return false
            }

            if (response.ok && response.body) {
                console.log('✅ Resuming existing stream')
                setRequestInProgress(true)
                setCanCancelStream(true)

                // Use the shared stream consumption logic
                await consumeStreamResponse(response)
                return true
            }
        } catch (error) {
            console.error('Error resuming stream:', error)
        }

        return false
    }, [consumeStreamResponse])


    // Helper function to set conversation state from messages
    const setConversationState = (messages: Message[], updateCache = false, newConversationId?: string, systemPrompt?: string | null) => {
        console.log('setConversationState', systemPrompt?.toString(), typeof systemPrompt === 'string')
        setMessages(messages)
        const isBookmarked = messages[0]?.info?.isBookmarked || false
        setIsBookmarked(isBookmarked)
        if (typeof systemPrompt === 'string') {
            console.log('setting conversation system prompt', systemPrompt)
            setConversationSystemPrompt(systemPrompt)
        }
        
        if (updateCache && newConversationId) {
            console.log('udpating cache from setConversationState')
            updateCachedConversation(newConversationId, messages, true, systemPrompt ?? undefined)
        }
    }

    // Enhanced loading conversation from db with better integration
    const loadConversationFromDb = async (newConversationId: string): Promise<{ messages: Message[], title: string, bookmarked: boolean, conversationLastSystemPrompt?: string } | null> => {
        try {
            if (!newConversationId) {
                console.error("No conversation ID available")
                return null
            }

            // Fetch messages from the database using the fetchConversationMessages function
            const { messages: dbMessages, conversationTitle, conversationBookmarked, conversationLastSystemPrompt } = await fetchConversationMessages(newConversationId)

            // Update messages state with fetched messages
            if (dbMessages && dbMessages.length > 0) {
                // Check if DB data is different from cache
                const cachedConversation = getCachedConversation(newConversationId)

                const cachedLen = cachedConversation?.messages?.length || 0
                const dbLen = dbMessages.length
                const contentsDiffer = !!cachedConversation && JSON.stringify(cachedConversation.messages) !== JSON.stringify(dbMessages)
                const countsDiffer = !!cachedConversation && dbLen !== cachedLen

                // Prefer DB whenever it differs from cache, but never override during an in-flight stream
                const shouldUpdateUI = !requestInProgress && (!cachedConversation || countsDiffer || contentsDiffer)

                // Only update UI if data is different
                if (shouldUpdateUI) {
                    console.log('WE SHOULD UPDATE UI', conversationLastSystemPrompt)
                    setConversationState(dbMessages, true, newConversationId, conversationLastSystemPrompt)
                } else if (conversationLastSystemPrompt !== undefined) {
                    // If messages unchanged but we got a prompt, still update local state
                    setConversationSystemPrompt(conversationLastSystemPrompt ?? null)
                }

                // Always update title
                setConversationTitle(conversationTitle)

                // Sync cache when safe: avoid clobbering in-flight streamed cache
                if (shouldUpdateUI) {
                    synchronizeWithDb(newConversationId, dbMessages)
                }
                
                return { 
                    messages: dbMessages, 
                    title: conversationTitle, 
                    bookmarked: conversationBookmarked,
                    conversationLastSystemPrompt
                }
            }
            
            return null
        } catch (error) {
            console.error("Error loading conversation:", error)
            toast.error("Failed to load conversation")
            return null
        }
    }

    const loadConversationData = async (newConversationId: string) => {
        if (!newConversationId) return

        setIsInitializing(true)
        let streamResumed = false
        let skeletonAlreadyHidden = false

        try {
            // Check for view switching data first
            const viewSwitchData = getLocalStorageValue({ 
                key: `conversation:${newConversationId}`, 
                deleteAfterGet: true 
            })

            if (viewSwitchData?.messages) {
                // Handle view switching case
                setConversationState(viewSwitchData.messages, true, newConversationId)
                setIsInitializing(false) // Hide skeleton immediately - we have messages
                skeletonAlreadyHidden = true
                streamResumed = await attemptStreamResumption(newConversationId)
            }
            else if (!isUserAuthenticated) {
                // Handle non-authenticated users
                const cachedConversation = getCachedConversation(newConversationId)
                if (cachedConversation) {
                    setConversationState(cachedConversation.messages)
                    setIsInitializing(false) // Hide skeleton immediately - we have messages
                    skeletonAlreadyHidden = true
                }
                streamResumed = await attemptStreamResumption(newConversationId)
            }
            else {
                // Handle authenticated users - check cache first for immediate display
                const cachedConversation = getCachedConversation(newConversationId)
                if (cachedConversation) {
                    setConversationState(cachedConversation.messages)
                    setIsInitializing(false) // Hide skeleton immediately - we have cached messages
                    skeletonAlreadyHidden = true
                }

                // Load from DB and attempt stream resumption in parallel
                const [dbResult, dbStreamResumed] = await Promise.all([
                    loadConversationFromDb(newConversationId),
                    attemptStreamResumption(newConversationId)
                ])
                
                streamResumed = dbStreamResumed
                
                // Update title if we got DB data
                if (dbResult?.title) {
                    setConversationTitle(dbResult.title)
                }
            }

            if (streamResumed) {
                console.log('✅ Stream resumed successfully')
                setIsStreamResumable(true)
            }
        } catch (error) {
            console.error('Error in loadConversationData:', error)
            toast.error('Failed to load conversation data')
        } finally {
            // Only hide skeleton if we haven't already done so
            if (!skeletonAlreadyHidden) {
                setIsInitializing(false)
            }
        }
    }




    // On mount check for error parameters
    useEffect(() => {
        function initializeConversation() {

            // Initialize conversation ID synchronously to avoid race conditions
            if (typeof window !== 'undefined') {
                const path = window.location.pathname
                if (path.startsWith('/chat/') && path !== '/chat') {
                    const idFromPath = path.replace('/chat/', '')
                    setConversationId(idFromPath)
                    loadConversationData(idFromPath)
                } else {
                    // Generate new conversation ID if not present
                    const newId = uuidv4()
                    setConversationId(newId)
                }
            }


            // Check for error parameter and display toast if present
            if (searchParams) {
                const error = searchParams.get('error')
                if (error) {
                    toast.error("Authentication Error", {
                        description: decodeURIComponent(error),
                        duration: 6000
                    })
                }
            }
        }

        initializeConversation()
    }, [])




    const addOrUpdateMessage = async (newMessage: Message) => {
        let isNewMessage = false

        // Model tracking is now handled by use-prompt-form

        // Add a new message or update existing one in the messages state
        setMessages((prevMessages) => {
            const existingIndex = prevMessages.findIndex(m => m.id === newMessage.id)
            
            if (existingIndex > -1) {
                const updatedMessages = prevMessages.map((msg, index) =>
                    index === existingIndex ? newMessage : msg
                )

                // Update cache with the new message array
                if (conversationId) {
                    console.log('udpating cache from addOrUpdateMessage inside if')
                    updateCachedConversation(conversationId, updatedMessages, false, conversationSystemPrompt ?? undefined)
                }

                return updatedMessages
            } else {
                isNewMessage = true
                const updatedMessages = [...prevMessages, newMessage]

                // Update cache with the new message array
                if (conversationId) {
                    console.log('udpating cache from addOrUpdateMessage inside else')
                    updateCachedConversation(conversationId, updatedMessages, false, conversationSystemPrompt ?? undefined)
                }

                return updatedMessages
            }
        })

        // Dispatch appropriate event based on whether we added or updated a message
        if (isNewMessage) {
            dispatchNewEvent('messageAdded', {})
        } else {
            dispatchNewEvent('messageUpdated', {})
        }
    }


    // Handle bookmarking messages or conversations
    const handleBookmark = async (type: 'conversation' | 'message', id?: string) => {
        try {
            // Common pattern: toggle bookmark status, update UI, then database
            const toggleBookmark = async ({ currentStatus, updateUI, updateDB }: { currentStatus: boolean, updateUI: (status: boolean) => void, updateDB: () => Promise<any> }) => {
                const newStatus = !currentStatus
                updateUI(newStatus)

                try {
                    await updateDB()
                } catch (error) {
                    updateUI(!newStatus)
                    throw error
                }
            }

            if (type === 'message') {
                if (!id) {
                    toast.error("Message ID is required when bookmarking a message")
                    return
                }

                const messageToUpdate = messages.find(msg => msg.id === id)
                if (messageToUpdate) {
                    await toggleBookmark({
                        currentStatus: !!messageToUpdate.info.isBookmarked,
                        updateUI: (status) => addOrUpdateMessage({
                            ...messageToUpdate,
                            info: { ...messageToUpdate.info, isBookmarked: status }
                        }),
                        updateDB: () => bookmarkMessage(id, !messageToUpdate.info.isBookmarked)
                    })
                }
            } else if (type === 'conversation') {
                await toggleBookmark({
                    currentStatus: isBookmarked,
                    updateUI: setIsBookmarked,
                    updateDB: () => bookmarkConversation(conversationId, !isBookmarked)
                })
            }
        } catch (error: any) {
            toast.error(error.message)
        }
    }



    const rerunRequest = async (parentMessageId: Message['id']) => {
        const parentMessage = messages.find(msg => msg.id === parentMessageId)
        if (parentMessage) {
            createNewRequest({ 
                userMessage: parentMessage, 
                onSuccess: () => {}, 
                onFailure: () => {} 
            })
        }
    }



    // This manages everything to do with creating new requests like formatting the request, splitting the request, and managing the response.
    const createNewRequest = async ({ userMessage, onSuccess, onFailure }: CreateNewRequest) => {
        const selectedModelId = userMessage.modelId

        // understand which type of request (image text) should be made
        const selectedModel = models[selectedModelId]

        // AUTHORIZE USER TO CHECK BALANCE IF PAID MODEL todo: add a first check for balance here not just in the api


        // Create a new message with proper type annotations
        const assistantMessage: Message = {
            id: uuidv4(),
            parentId: userMessage.id,
            createdAt: new Date(Date.now() + 100),
            role: 'assistant',
            content: '',
            modelId: userMessage.modelId,
            info: {
                type: 'assistantTextMessage' as const,
                voiceMode: userMessage.info.voiceMode,
                status: {
                    isLoading: true,
                    status: 'pending',
                    steps: [{
                        type: 'authorization',
                        status: 'pending'
                    }]
                }
            },
            files: [],
        }

        console.log('created assistant message, userMessage:', userMessage)

        console.log('🆕 Created assistant message with ID:', assistantMessage.id)
        // Capture the latest onFailure so we can notify listeners if the stream emits an error event
        latestOnFailureRef.current = onFailure || null
        addOrUpdateMessage(assistantMessage)
        userMessage = addLogToMessage(userMessage, 'assistant_message_created')

        // Get client data including guest user identifiers
        const clientData = {
            ...await checkOrCreateUserIdentifiers()
        }

        // Format the request object
        const formattedRequest = formatRequestObject({
            userMessage,
            assistantMessage,
            messages,
            conversationSystemPrompt: conversationSystemPrompt ?? undefined,
            conversationId,
            keepFilesInRequest,
            // options,
            userId: userContextValue?.id || '',
            user: userContextValue || defaultUser,
            clientData
        })

        // Make a basic check for eligibility
        const { isAuthorized, errorType } = await checkUserEligibility(
            models[formattedRequest.inputObject.modelId],
            formattedRequest,
            userContextValue
        )

        userMessage = addLogToMessage(userMessage, `eligibility_check_${isAuthorized ? 'passed' : 'failed'}`)

        if (!isAuthorized) {
            // Get the error message from the error code
            const errorMessage = errorType && ERROR_DEFINITIONS[errorType]
                ? ERROR_DEFINITIONS[errorType].message
                : "An unknown error occurred";

            toast.error(errorMessage)
            const failedAssistantMessage: Message = {
                ...formattedRequest.assistantMessage,
                info: {
                    ...formattedRequest.assistantMessage.info,
                    status: {
                        ...formattedRequest.assistantMessage.info.status,
                        isLoading: false,
                        status: 'failed',
                        steps: []
                    }
                },
                error: {
                    message: errorMessage,
                    errorType: errorType
                }
            }
            addOrUpdateMessage(failedAssistantMessage)
            // Remove the transient failed assistant message from cache right away
            if (conversationId) {
                removeMessageFromCache(conversationId, failedAssistantMessage.id)
            }

            // Call the failure callback if provided
            if (onFailure && errorMessage) {
                onFailure(errorMessage)
            }

            return
        } else {
            onSuccess?.()
            window.history.pushState({}, '', '/chat/' + conversationId)
            forceSavePendingDebouncedUpdates()
        }

        // Then make the LLM request
        await makeLLMRequest({
            formattedRequestObject: formattedRequest,
        })
    }



    const makeLLMRequest = async ({
        formattedRequestObject,
    }: {
        formattedRequestObject: FormattedRequestObject,
    }) => {
        setRequestInProgress(true)
        setCanCancelStream(true)
        
        try {
            const response = await fetch('/api/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formattedRequestObject),
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            // Use the shared stream consumption logic
            await consumeStreamResponse(response)
            //only for testing

        } catch (err) {
            console.error('Error streaming data:', err)
            toast.error('Error connecting to the server')
            // bubble up to any listener so UI (prompt form) can restore user draft
            latestOnFailureRef.current?.('Error connecting to the server')
            latestOnFailureRef.current = null
            // Mark the assistant message as failed and clear steps
            setMessages(prev => prev.map(msg => {
                if (msg.id !== formattedRequestObject.assistantMessage.id) return msg
                return {
                    ...msg,
                    info: {
                        ...msg.info,
                        status: {
                            ...msg.info.status,
                            isLoading: false,
                            status: 'failed',
                            steps: []
                        }
                    }
                }
            }))
            // Also remove the transient failed message from cache
            if (conversationId) {
                removeMessageFromCache(conversationId, formattedRequestObject.assistantMessage.id)
            }
        } finally {
            setRequestInProgress(false)
            setCanCancelStream(false)
        }
    }

    const handleUpdateTitle = async (newTitle: string, saveToDb: boolean = true) => {
        const oldTitle = conversationTitle
        try {
            setConversationTitle(newTitle)

            if (saveToDb && conversationId) {
                await upsertChatTitle(conversationId, newTitle)
            }
        } catch (error: any) {
            toast.error(error.message)
            setConversationTitle(oldTitle)
        }
    }

    // Stream cancellation function
    const cancelCurrentStream = useCallback(async () => {
        if (conversationId && requestInProgress) {
            console.log('🛑 Cancelling stream for conversation:', conversationId)
            
            try {
                // Call your cancel endpoint
                const response = await fetch(`/api/generate/${conversationId}/cancel`, {
                    method: 'POST',
                })

                if (response.ok) {
                    console.log('✅ Stream cancelled successfully')
                    toast.info('Stream cancelled')
                }
            } catch (error) {
                console.error('Error cancelling stream:', error)
            }

            // Close the reader if it exists
            // if (readerRef.current) {
            //     try {
            //         await readerRef.current.cancel()
            //     } catch (e) {
            //         // Reader might already be closed
            //     }
            //     readerRef.current = null
            // }

            setRequestInProgress(false)
            setCanCancelStream(false)
        }
    }, [conversationId, requestInProgress])


    return {
        managerError,
        isInitializing,
        conversationId,
        setConversationId,
        messages,
        isBookmarked,
        handleBookmark,
        addOrUpdateMessage,
        createNewRequest, 
        rerunRequest,
        keepFilesInRequest,
        setKeepFilesInRequest,
        handleUpdateTitle,
        conversationTitle,
        conversationSystemPrompt,
        setConversationSystemPrompt,

        requestInProgress,

        // New resumable streaming properties
        canCancelStream,
        cancelCurrentStream,
        isStreamResumable,
    }

}